import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import Modal from './Modal';
import EventButton from './Buttons/EventButton';

function MitigationTable({ title, content: ContentComponent, srm }) {
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const safetyRequirements = useMemo(
    () => srm?.safety_requirements || [],
    [srm?.safety_requirements],
  );
  const causes = useMemo(() => srm?.causes || [], [srm?.causes]);
  const systemStates = useMemo(() => srm?.system_states || [], [srm?.system_states]);
  const [content, setContent] = useState([]);
  const handleEditClick = (item) => {
    setEditItem(item);
    setShowModal(true);
  };
  const handleAddClick = () => {
    setEditItem(null);
    setShowModal(true);
  };
  useEffect(() => {
    let tableContent;
    if (title === 'Safety Requirements') {
      tableContent = safetyRequirements.map((safetyRequirement) => (
        <tr key={safetyRequirement.safetyRequirement}>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{safetyRequirement.safetyRequirement}</td>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{safetyRequirement.safetyRequirementReportedBy}</td>
          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
            <button
              type="button"
              id={safetyRequirement.safetyRequirement}
              onClick={() => handleEditClick(safetyRequirement)}
              className="flex w-full items-center py-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white text-gray-700 dark:text-gray-200"
            >
              <svg
                aria-hidden="true"
                className="mr-1 -ml-1 w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                <path
                  fillRule="evenodd"
                  d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                  clipRule="evenodd"
                />
              </svg>
              Edit
            </button>
          </td>
          <td className="px-4 py-3 flex items-center justify-end" />
        </tr>
      ));
    } else if (title === 'Mitigations Implementated') {
      tableContent = causes.map((cause) => (
        <tr key={cause.id}>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{cause.main_cause_name}</td>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{cause.sub_cause_name}</td>
          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
            <button
              type="button"
              id={cause.id}
              onClick={() => handleEditClick(cause)}
              className="flex w-full items-center py-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white text-gray-700 dark:text-gray-200"
            >
              <svg
                aria-hidden="true"
                className="mr-1 -ml-1 w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                <path
                  fillRule="evenodd"
                  d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                  clipRule="evenodd"
                />
              </svg>
              Edit
            </button>
          </td>
          <td className="px-4 py-3 flex items-center justify-end" />
        </tr>
      ));
    } else if (title === 'Performance Targets') {
      tableContent = systemStates.map((systemState) => (
        <tr key={systemState.id}>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{systemState.details}</td>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{systemState.reportedBy}</td>
          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
            <button
              type="button"
              id={systemState.id}
              onClick={() => handleEditClick(systemState)}
              className="flex w-full items-center py-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white text-gray-700 dark:text-gray-200"
            >
              <svg
                aria-hidden="true"
                className="mr-1 -ml-1 w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                <path
                  fillRule="evenodd"
                  d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                  clipRule="evenodd"
                />
              </svg>
              Edit
            </button>
          </td>
          <td className="px-4 py-3 flex items-center justify-end" />
        </tr>
      ));
    }
    setContent(tableContent);
  }, [title, safetyRequirements, causes, systemStates]);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-500">{title}</h1>
        </div>
        <div className="mt-4 pt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <EventButton
            onSubmit={handleAddClick}
            linktext={`Add ${title}`}
          />
          {showModal && (
            <Modal
              editItem={editItem || ''}
              title={title}
              content={ContentComponent}
              srm={srm}
              onClose={() => setShowModal(false)}
            />
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        {content.length !== 0 && (
          <table className="w-full my-2 text-sm text-left text-gray-400 dark:text-gray-400">
            <thead className="text-xs text-gray-300 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-4 py-3">Description</th>
                <th scope="col" className="px-4 py-3">{title === 'Causes' ? 'Sub Cause Name' : 'By'}</th>
                <th scope="col" className="px-4 py-3">
                  <span>Actions</span>
                </th>
                <th scope="col" className="px-4 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {content}
            </tbody>
          </table>
        )}
        {content.length === 0 && (
          <p className="text-sm text-gray-500">
            No
            {title.toLowerCase()}
            {' '}
            available.
          </p>
        )}
      </div>
    </div>
  );
}

MitigationTable.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.elementType.isRequired,
  // srm: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  srm: PropTypes.object.isRequired,
};

// MitigationTable.defaultProps = {
//   srm: null,
// };

export default MitigationTable;
export const handleSave = async (endpoint, formData, method = 'POST') => {
  try {
    // Get token from localStorage
    const token = localStorage.getItem('ans-sms');
    const authToken = token ? JSON.parse(token).token : null;

    const headers = {
      'Content-Type': 'application/json',
      ...(authToken && { Authorization: `Bearer ${authToken}` }),
    };

    if (method === 'POST') {
      await axios.post(`http://localhost:8080/${endpoint}`, formData, { headers });
      window.location.reload(false);
    } else if (method === 'PATCH') {
      await axios.patch(`http://localhost:8080/${endpoint}`, formData, { headers });
      window.location.reload(false);
      window.scrollTo(0, window.location);
    }
  } catch (error) {
    // Error handling for API requests
  }
};
